const STORAGE_KEY_VERSION = 'playgrid-audio-storage-version';
const STORAGE_KEY_SFX = 'playgrid-audio-sfx-muted';
const STORAGE_KEY_MUSIC = 'playgrid-audio-music-muted';
const DEFAULT_VERSION = '4';

export const DEFAULT_MUSIC_ENABLED = true;
export const DEFAULT_SOUND_ENABLED = false;

export const DEFAULT_MUSIC_MUTED = !DEFAULT_MUSIC_ENABLED;
export const DEFAULT_SOUND_MUTED = !DEFAULT_SOUND_ENABLED;

export type AudioStorageSnapshot = {
  sfxMuted: boolean;
  musicMuted: boolean;
  wasReset: boolean;
};

const isBooleanString = (value: string | null): value is 'true' | 'false' => {
  return value === 'true' || value === 'false';
};

const toBoolean = (value: string | null, fallback: boolean) => {
  if (!isBooleanString(value)) return fallback;
  return value === 'true';
};

const readStoredMuted = (key: string, fallback: boolean): boolean => {
  try {
    return toBoolean(localStorage.getItem(key), fallback);
  } catch {
    return fallback;
  }
};

const writeStoredMuted = (key: string, muted: boolean): void => {
  localStorage.setItem(key, String(muted));
  localStorage.setItem(STORAGE_KEY_VERSION, DEFAULT_VERSION);
};

export const sanitizeAudioStorage = (): AudioStorageSnapshot => {
  let didReset = false;

  try {
    const version = localStorage.getItem(STORAGE_KEY_VERSION);
    if (version !== DEFAULT_VERSION) {
      localStorage.removeItem(STORAGE_KEY_SFX);
      localStorage.removeItem(STORAGE_KEY_MUSIC);
      localStorage.setItem(STORAGE_KEY_VERSION, DEFAULT_VERSION);
      didReset = true;
    }
  } catch (error) {
    console.warn('[audio-storage] Failed to validate audio preferences', error);
    didReset = true;
  }

  const sfxMuted = readStoredMuted(STORAGE_KEY_SFX, DEFAULT_SOUND_MUTED);
  const musicMuted = readStoredMuted(STORAGE_KEY_MUSIC, DEFAULT_MUSIC_MUTED);

  if (didReset) {
    console.info('[audio-storage] Audio preferences reset to defaults');
  }

  console.log('[audio-check] audioStorage.sanitize -> snapshot', {
    sfxMuted,
    musicMuted,
    wasReset: didReset,
  });

  return {
    sfxMuted,
    musicMuted,
    wasReset: didReset,
  };
};

export const persistSfxMuted = (sfxMuted: boolean): void => {
  try {
    writeStoredMuted(STORAGE_KEY_SFX, sfxMuted);
    console.log('[audio-check] audioStorage.persist -> sfxMuted', sfxMuted);
  } catch (error) {
    console.warn('[audio-storage] Failed to persist sound preference', error);
  }
};

export const persistMusicMuted = (musicMuted: boolean): void => {
  try {
    writeStoredMuted(STORAGE_KEY_MUSIC, musicMuted);
    console.log('[audio-check] audioStorage.persist -> musicMuted', musicMuted);
  } catch (error) {
    console.warn('[audio-storage] Failed to persist music preference', error);
  }
};

export const maybeResetAudioStorageAfterReload = () => {
  try {
    const navEntries = performance.getEntriesByType(
      'navigation'
    ) as PerformanceNavigationTiming[];
    const navType =
      navEntries[0]?.type ?? (performance as any).navigation?.type;

    if (navType === 'reload') {
      sanitizeAudioStorage();
    }
  } catch (error) {
    console.warn('[audio-storage] Failed to perform reload reset check', error);
  }
};

export const AUDIO_STORAGE_KEYS = {
  SFX: STORAGE_KEY_SFX,
  MUSIC: STORAGE_KEY_MUSIC,
  VERSION: STORAGE_KEY_VERSION,
} as const;
